import React, { useEffect, useState, useCallback } from 'react';
import { useTonConnectUI } from '@tonconnect/ui-react';
import { api } from '../context/GameContext';
import { Star, Sparkles, X, Loader2, ChevronRight, Clock } from 'lucide-react';

/**
 * Seller flow for listing a Masterpiece NFT as a player-auction lot.
 *
 *   1. Preview pricing for the chosen card (starting price + buyout).
 *   2. Pick a duration (2h / 6h / 12h, each with a Stars listing fee).
 *   3. Pay the Stars listing fee via Telegram invoice.
 *   4. Sign a TonConnect NFT-transfer message → NFT moves to escrow.
 *   5. Backend verifies escrow ownership on-chain, lot enters auction queue.
 */
const SellModal = ({ card, language, onClose, onListed }) => {
  const [tonConnectUI] = useTonConnectUI();
  const [preview, setPreview] = useState(null);
  const [durationHours, setDurationHours] = useState(null);
  const [customPrice, setCustomPrice] = useState(''); // empty = use server default
  const [step, setStep] = useState('preview'); // preview → paying → transferring → verifying → done
  const [draftId, setDraftId] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const isRu = language === 'ru';

  // Step 1 — fetch starting/buyout + duration options
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post('/auction/sell/preview', { card_id: card.id });
        if (!cancelled) {
          setPreview(data);
          if (data.duration_options?.length) {
            setDurationHours(data.duration_options[0].hours);
          }
          // Pre-fill the custom-price input with the server-computed minimum
          setCustomPrice(String(data.starting_price || ''));
          // If a paid draft is resumable, surface its draft_id so the
          // Resend-Gas button works without going through handleList.
          if (data.resumable_draft?.draft_id) {
            setDraftId(data.resumable_draft.draft_id);
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || (isRu ? 'Ошибка загрузки' : 'Could not load'));
      }
    })();
    return () => { cancelled = true; };
  }, [card.id, isRu]);

  const pollDraftReady = useCallback(async (id, maxAttempts = 30) => {
    // Poll /auction/sell/draft/:id every 2s until status == fee_paid (or timeout)
    for (let i = 0; i < maxAttempts; i++) {
      try {
        const { data } = await api.get(`/auction/sell/draft/${id}`);
        if (data.expired) {
          throw new Error(isRu
            ? `Срок восстановления листинга истёк (${data.resume_window_hours || 12}ч). Обратитесь в поддержку.`
            : `Listing resume window expired (${data.resume_window_hours || 12}h). Please contact support.`);
        }
        if (data.status === 'fee_paid' || data.status === 'awaiting_escrow') {
          return data;
        }
        if (data.status === 'cancelled') {
          throw new Error(isRu ? 'Лот отменён' : 'Draft cancelled');
        }
      } catch (e) {
        if (e.response?.status === 404) throw e;
        if (e.message?.includes('expired') || e.message?.includes('истёк')) throw e;
      }
      await new Promise(r => setTimeout(r, 2000));
    }
    throw new Error(isRu ? 'Платёж не подтверждён' : 'Payment not confirmed');
  }, [isRu]);

  // Wait until the platform has finished pre-funding the seller's wallet
  // with TON gas, then wait an extra ~12 seconds for the on-chain block
  // confirmation so the seller's wallet sees the new balance before we
  // open the TonConnect prompt. Without this delay the wallet would
  // show "insufficient balance" and the user would have to manually
  // retry, defeating the "pay only in Stars" UX.
  const pollGasFunded = useCallback(async (id, maxAttempts = 20) => {
    for (let i = 0; i < maxAttempts; i++) {
      try {
        const { data } = await api.get(`/auction/sell/draft/${id}`);
        if (data.expired) {
          throw new Error(isRu
            ? `Срок восстановления листинга истёк (${data.resume_window_hours || 12}ч). Обратитесь в поддержку.`
            : `Listing resume window expired (${data.resume_window_hours || 12}h). Please contact support.`);
        }
        if (data.gas_fund_error) {
          throw new Error(isRu
            ? `Не удалось пополнить кошелёк: ${data.gas_fund_error.slice(0, 80)}`
            : `Wallet pre-fund failed: ${data.gas_fund_error.slice(0, 80)}`);
        }
        if (data.gas_sent_at) {
          // Give TON ~12s to confirm the pre-fund on-chain
          await new Promise(r => setTimeout(r, 12000));
          return data;
        }
      } catch (e) {
        if (e.message?.includes('pre-fund') || e.message?.includes('expired') || e.message?.includes('истёк')) throw e;
      }
      await new Promise(r => setTimeout(r, 1500));
    }
    throw new Error(isRu ? 'Не удалось подготовить кошелёк' : 'Could not prepare wallet');
  }, [isRu]);

  const handleList = async () => {
    if (!durationHours || busy) return;
    setBusy(true);
    setError(null);
    try {
      setStep('paying');
      const minPrice = Number(preview?.starting_price || 0);
      const userPrice = Number(customPrice);
      // Frontend validation (server validates again as last line of defense)
      if (!Number.isFinite(userPrice) || userPrice < minPrice) {
        throw new Error(isRu
          ? `Цена должна быть ≥ ${minPrice} ⭐`
          : `Price must be ≥ ${minPrice} ⭐`);
      }
      const { data: init } = await api.post('/auction/sell/init', {
        card_id: card.id,
        duration_hours: durationHours,
        starting_price: Math.floor(userPrice),
      });
      setDraftId(init.draft_id);

      // If the backend handed us back an existing fee_paid /
      // awaiting_escrow draft (seller had a stuck listing within the
      // 12h resume window) it skips the Stars invoice entirely —
      // their previous fee is still good. Jump straight to the gas
      // wait + TonConnect step instead of trying to charge them
      // again. This is what surfaces the "you already started a
      // sale" error away from the user.
      if (init.already_paid) {
        const draft = await pollDraftReady(init.draft_id);
        if (!draft.transfer) {
          throw new Error(isRu ? 'Эскроу-кошелёк недоступен' : 'Escrow address unavailable');
        }
        // Gas TON was already pre-funded in the earlier attempt — wait
        // out the remainder of the 12s confirmation window if needed,
        // then sign. We do NOT call pollGasFunded here because that
        // helper waits for a *new* gas_sent_at to appear (which won't,
        // per the one-shot policy).
        setStep('preparing');
        const gasAge = draft.gas_sent_at
          ? Math.max(0, Date.now() - new Date(draft.gas_sent_at).getTime())
          : 0;
        if (gasAge < 12000) {
          await new Promise(r => setTimeout(r, 12000 - gasAge));
        }
        await signAndConfirm(init.draft_id, draft);
        return;
      }

      const tg = window.Telegram?.WebApp;
      // Open Stars invoice
      await new Promise((resolve, reject) => {
        if (!tg?.openInvoice) {
          window.open(init.invoice_link, '_blank');
          // best-effort: keep polling regardless
          resolve();
          return;
        }
        tg.openInvoice(init.invoice_link, (status) => {
          if (status === 'paid') return resolve();
          if (status === 'cancelled' || status === 'failed') {
            return reject(new Error(isRu ? 'Платёж отменён' : 'Payment cancelled'));
          }
          // pending → resolve and let poll handle it
          resolve();
        });
      });

      // Wait for webhook to mark draft as fee_paid + return transfer payload
      setStep('transferring');
      const draft = await pollDraftReady(init.draft_id);
      if (!draft.transfer) {
        throw new Error(isRu ? 'Эскроу-кошелёк недоступен' : 'Escrow address unavailable');
      }

      // Wait until platform pre-funded the seller's TON wallet
      // (gas_sent_at appears) + 12s for on-chain confirmation. This is
      // the "Подготавливаем кошелёк..." step in the UI.
      setStep('preparing');
      await pollGasFunded(init.draft_id);

      await signAndConfirm(init.draft_id, draft);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || (isRu ? 'Ошибка' : 'Error');
      setError(msg);
      setStep('preview');
    } finally {
      setBusy(false);
    }
  };

  // Resume an existing paid draft — used when the Stars listing fee was
  // already charged but the TonConnect step failed (e.g. wallet showed
  // "insufficient balance" before the gas pre-fund landed). Skips
  // payment + draft creation; goes straight to gas + sign.
  const handleResume = async () => {
    const resumable = preview?.resumable_draft;
    if (!resumable || busy) return;
    setBusy(true);
    setError(null);
    try {
      setDraftId(resumable.draft_id);
      setStep('preparing');
      // Fetch latest draft state (status, transfer payload, etc.)
      const { data: draft } = await api.get(`/auction/sell/draft/${resumable.draft_id}`);
      if (!draft.transfer) {
        throw new Error(isRu ? 'Эскроу-кошелёк недоступен' : 'Escrow address unavailable');
      }
      // Per platform policy, gas TON is sent EXACTLY ONCE per listing
      // (via the Stars-payment webhook). We do NOT retry pre-fund on
      // Resume — if the original 0.07 TON didn't land, the seller
      // needs to contact support. Here we just give the original
      // pre-fund a few extra seconds to confirm on-chain in case the
      // Resume happens immediately after the Stars payment.
      const gasAge = draft.gas_sent_at
        ? Math.max(0, Date.now() - new Date(draft.gas_sent_at).getTime())
        : 0;
      if (gasAge < 12000) {
        await new Promise(r => setTimeout(r, 12000 - gasAge));
      }
      await signAndConfirm(resumable.draft_id, draft);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || (isRu ? 'Ошибка' : 'Error');
      setError(msg);
      setStep('preview');
    } finally {
      setBusy(false);
    }
  };

  // Resend gas: deprecated. Per platform policy gas TON is sent exactly
  // ONCE per listing attempt (via the Stars-payment webhook). Backend
  // returns 429 for any resend attempt, so this UI path was removed.

  // Common sign + on-chain confirm logic shared by handleList and handleResume
  const signAndConfirm = async (draftIdLocal, draft) => {
    // Guard against expired/lost TonConnect session. The SDK throws
    // `[TON_CONNECT_SDK_ERROR] Connect wallet to send a transaction`
    // when its internal wallet reference is null — typical when the
    // user opened the Mini App in a new tab, the Tonkeeper deeplink
    // session expired, or the page got reloaded between connect and
    // send. We give the SDK ~6s to rehydrate from localStorage, and
    // if nothing surfaces we proactively open the connect modal so
    // the seller can re-link before we retry the transfer.
    if (!tonConnectUI.wallet) {
      for (let i = 0; i < 12 && !tonConnectUI.wallet; i++) {
        await new Promise(r => setTimeout(r, 500));
      }
      if (!tonConnectUI.wallet) {
        try {
          await tonConnectUI.openModal();
        } catch (_) { /* user closed picker */ }
        // Wait up to 60s for the user to connect their wallet
        for (let i = 0; i < 120 && !tonConnectUI.wallet; i++) {
          await new Promise(r => setTimeout(r, 500));
        }
        if (!tonConnectUI.wallet) {
          throw new Error(isRu
            ? 'Кошелёк TON не подключён. Подключите кошелёк и попробуйте снова.'
            : 'TON wallet not connected. Connect your wallet and try again.');
        }
      }
    }

    // TonConnect transfer of the NFT item → escrow
    const tx = {
      validUntil: Math.floor(Date.now() / 1000) + 600,
      messages: [{
        address: draft.transfer.nft_address,
        amount: draft.transfer.amount_nano,
        payload: draft.transfer.payload_b64,
      }],
    };
    setStep('transferring');
    await tonConnectUI.sendTransaction(tx);

    // Confirm on backend (verifies on-chain owner == escrow). Retry up to ~60s.
    setStep('verifying');
    let confirmed = null;
    let lastErr = null;
    for (let i = 0; i < 20; i++) {
      try {
        const { data } = await api.post('/auction/sell/confirm', { draft_id: draftIdLocal });
        if (data.ok) { confirmed = data; break; }
      } catch (e) {
        lastErr = e;
        if (e.response?.status !== 409) throw e;
      }
      await new Promise(r => setTimeout(r, 3500));
    }
    if (!confirmed) throw lastErr || new Error(isRu ? 'Не удалось подтвердить эскроу' : 'Escrow not confirmed');

    setStep('done');
    if (onListed) onListed(confirmed.lot_id);
  };

  return (
    <div
      data-testid="sell-modal"
      className="fixed inset-0 bg-black/85 z-[60] flex items-end sm:items-center justify-center px-3 pb-6 sm:p-6"
      // Backdrop click intentionally does NOT close the modal — users
      // frequently tap outside the price input to dismiss the mobile
      // keyboard and we don't want that to nuke their work. They must
      // tap the X button (top-right) to dismiss.
    >
      <div className="w-full max-w-lg max-h-[92vh] overflow-y-auto bg-gradient-to-br from-[#1a1a2e] via-[#161630] to-[#10101c] border border-[#A855F7]/30 rounded-2xl p-5 shadow-2xl">
        <div className="flex items-start justify-between mb-3 gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#A855F7]/70 font-semibold">
              {isRu ? 'Выставить на аукцион' : 'List for auction'}
            </div>
            <div className="text-base font-semibold text-[#F5F5F7] mt-0.5 break-words leading-snug" data-testid="sell-modal-title">
              {card.title}
            </div>
            <div className="text-xs text-[#A855F7]/70 break-words">{card.artist}</div>
          </div>
          <button
            data-testid="sell-modal-close"
            onClick={onClose}
            className="shrink-0 text-[#F5F5F7]/40 hover:text-[#F5F5F7] active:scale-90 transition-transform"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!preview && !error && (
          <div className="flex items-center justify-center py-8 text-[#F5F5F7]/60 text-sm gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> {isRu ? 'Загрузка…' : 'Loading…'}
          </div>
        )}

        {preview && (
          <>
            {/* Resume banner — shown when a paid draft exists for this card */}
            {preview.resumable_draft && step === 'preview' && (
              <div className="rounded-xl bg-[#FACC15]/10 border border-[#FACC15]/30 p-3 mb-4">
                <div className="text-[11px] font-semibold text-[#FACC15] uppercase tracking-wider mb-1">
                  {isRu ? '⚡ Продолжить листинг' : '⚡ Resume listing'}
                </div>
                <div className="text-[12px] text-[#F5F5F7]/80 leading-snug mb-2">
                  {isRu
                    ? `Вы уже оплатили листинг (${preview.resumable_draft.listing_fee_stars} ⭐). Можно сразу подписать перевод NFT — оплачивать повторно не нужно.`
                    : `You already paid the listing fee (${preview.resumable_draft.listing_fee_stars} ⭐). Sign the NFT transfer to finish — no need to pay again.`}
                </div>
                <button
                  data-testid="sell-resume-btn"
                  onClick={handleResume}
                  disabled={busy}
                  className="w-full py-2 rounded-lg bg-gradient-to-r from-[#FACC15] to-[#F59E0B] text-black font-bold text-xs flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50"
                >
                  {isRu ? 'Продолжить' : 'Continue listing'} →
                </button>
              </div>
            )}

            {/* Starting price input — seller chooses, floor = computed min */}
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#F5F5F7]/40 font-semibold mb-1.5">
              {isRu ? 'Цена' : 'Price'}
            </div>
            <div className={`relative mb-1 ${step !== 'preview' ? 'opacity-60 pointer-events-none' : ''}`}>
              <Star className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#FACC15]" fill="#FACC15" stroke="#FACC15" />
              <input
                data-testid="sell-price-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                enterKeyHint="done"
                min={preview.starting_price}
                step={1}
                value={customPrice}
                onChange={(e) => setCustomPrice(e.target.value.replace(/[^0-9]/g, ''))}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                disabled={busy}
                className="w-full bg-[#0f0f1f]/80 border border-[#A855F7]/25 rounded-xl pl-10 pr-16 py-3 text-lg font-bold text-[#F5F5F7] focus:outline-none focus:border-[#A855F7] focus:ring-2 focus:ring-[#A855F7]/30 placeholder:text-[#5A5A66]"
                placeholder={String(preview.starting_price || 0)}
              />
              <button
                type="button"
                data-testid="sell-price-apply"
                onClick={(e) => {
                  // Dismiss the on-screen keyboard by blurring the input.
                  // Necessary because the iOS Telegram WebApp number pad
                  // ships without a Done key.
                  const input = e.currentTarget.parentElement?.querySelector('input');
                  if (input) input.blur();
                }}
                disabled={busy}
                className="absolute right-1 top-1/2 -translate-y-1/2 px-3 py-1.5 rounded-lg bg-[#A855F7]/20 hover:bg-[#A855F7]/35 active:scale-95 text-[#A855F7] text-xs font-semibold border border-[#A855F7]/30 transition-all disabled:opacity-50"
              >
                {isRu ? 'OK' : 'OK'}
              </button>
            </div>
            <div className="flex items-center justify-between text-[10px] text-[#F5F5F7]/50 mb-3 px-1">
              <button
                type="button"
                onClick={() => !busy && setCustomPrice(String(preview.starting_price))}
                disabled={busy}
                className="underline text-[#A855F7]/70 hover:text-[#A855F7] disabled:opacity-50"
              >
                {isRu ? `мин: ${preview.starting_price?.toLocaleString()} ⭐` : `min: ${preview.starting_price?.toLocaleString()} ⭐`}
              </button>
              <span>
                {isRu
                  ? `Выкуп: ×3 текущей ставки`
                  : `Buyout: ×3 of current bid`}
              </span>
            </div>
            <div className="rounded-lg bg-[#0f0f1f]/60 border border-[#A855F7]/15 p-2.5 mb-4">
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-[#F5F5F7]/60">{isRu ? 'Выкуп при старте' : 'Buyout at start'}</span>
                <span className="flex items-center gap-1 text-[#A855F7] font-semibold text-sm">
                  <Star className="w-3 h-3" fill="#A855F7" stroke="#A855F7" />
                  {Math.max(Number(customPrice) || 0, preview.starting_price || 0) * 3 || '—'}
                </span>
              </div>
            </div>

            {/* Duration picker */}
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#F5F5F7]/40 font-semibold mb-2">
              {isRu ? 'Длительность лота' : 'Lot duration'}
            </div>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {(preview.duration_options || []).map(opt => {
                const selected = durationHours === opt.hours;
                return (
                  <button
                    key={opt.hours}
                    data-testid={`sell-duration-${opt.hours}h`}
                    onClick={() => !busy && setDurationHours(opt.hours)}
                    disabled={busy}
                    className={`rounded-xl p-2.5 text-center transition-all active:scale-95 border ${
                      selected
                        ? 'bg-[#A855F7]/20 border-[#A855F7] shadow-[0_0_18px_-6px_#A855F7]'
                        : 'bg-[#0f0f1f]/60 border-[#A855F7]/15 hover:border-[#A855F7]/35'
                    } ${busy ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className={`flex items-center justify-center gap-1 text-sm font-bold ${selected ? 'text-[#F5F5F7]' : 'text-[#F5F5F7]/80'}`}>
                      <Clock className="w-3 h-3" />
                      {opt.hours}h
                    </div>
                    <div className={`flex items-center justify-center gap-0.5 text-[11px] mt-1 ${selected ? 'text-[#FACC15]' : 'text-[#FACC15]/70'}`}>
                      <Star className="w-2.5 h-2.5" fill="currentColor" stroke="currentColor" />
                      {opt.listing_fee_stars}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Steps progress */}
            {step !== 'preview' && (
              <div className="rounded-lg bg-[#0f0f1f]/70 border border-[#A855F7]/15 p-2.5 mb-3 text-[11px] text-[#F5F5F7]/80 space-y-1">
                <Step done={['preparing','transferring','verifying','done'].includes(step)} active={step==='paying'}>
                  {isRu ? '1. Оплатить листинг ⭐' : '1. Pay listing fee ⭐'}
                </Step>
                <Step done={['transferring','verifying','done'].includes(step)} active={step==='preparing'}>
                  {isRu ? '2. Отправляем TON на ваш кошелёк' : '2. Sending TON to your wallet'}
                </Step>
                <Step done={['verifying','done'].includes(step)} active={step==='transferring'}>
                  {isRu ? '3. Подписать перевод NFT в эскроу' : '3. Sign NFT transfer to escrow'}
                </Step>
                <Step done={step==='done'} active={step==='verifying'}>
                  {isRu ? '4. Проверка on-chain' : '4. On-chain verification'}
                </Step>
              </div>
            )}

            {error && (
              <div className="text-[12px] text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg px-3 py-2 mb-3" data-testid="sell-error">
                {error}
                {draftId && (error.toLowerCase().includes('insufficient') ||
                              error.toLowerCase().includes('недостаточно') ||
                              error.toLowerCase().includes('wallet') ||
                              error.toLowerCase().includes('кошел')) && (
                  <p
                    data-testid="sell-contact-support-hint"
                    className="mt-2 text-[11px] text-[#FDE68A]/85 leading-snug"
                  >
                    {isRu
                      ? 'Газ TON отправляется один раз за листинг. Если он не дошёл — напишите в поддержку с ID черновика.'
                      : 'Gas TON is sent once per listing attempt. If it didn\u2019t arrive — contact support with the draft ID.'}
                  </p>
                )}
              </div>
            )}

            {step === 'done' ? (
              <button
                data-testid="sell-done-close"
                onClick={onClose}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-[#A855F7] to-[#9333EA] text-white font-semibold text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform"
              >
                <Sparkles className="w-4 h-4" />
                {isRu ? 'Лот в очереди!' : 'Listed!'}
              </button>
            ) : (
              <button
                data-testid="sell-confirm-btn"
                onClick={handleList}
                disabled={busy || !durationHours}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-[#A855F7] to-[#9333EA] text-white font-semibold text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {step === 'paying' && (isRu ? 'Ожидаем оплату…' : 'Awaiting payment…')}
                    {step === 'preparing' && (isRu ? 'Отправляем TON…' : 'Sending TON…')}
                    {step === 'transferring' && (isRu ? 'Подпишите перевод в TON…' : 'Sign in TonConnect…')}
                    {step === 'verifying' && (isRu ? 'Проверяем on-chain…' : 'Verifying on-chain…')}
                  </>
                ) : (
                  <>
                    {isRu ? 'Выставить' : 'List for sale'}
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </button>
            )}

            <div className="text-[10px] text-[#F5F5F7]/30 mt-3 leading-relaxed">
              {isRu
                ? '⚠️ Картина будет переведена в эскроу-кошелёк платформы до конца аукциона. Если не продастся — вернётся вам автоматически.\n\n💎 TON-комиссия включена в звёзды листинга — кошелёк продавца её не платит.'
                : '⚠️ Your painting will be transferred to the platform escrow until the auction ends. If unsold, it returns to you automatically.\n\n💎 TON gas fee is included in the Stars listing fee — your wallet pays nothing.'}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Step = ({ done, active, children }) => (
  <div className={`flex items-center gap-2 ${done ? 'text-[#10B981]' : active ? 'text-[#A855F7]' : 'text-[#F5F5F7]/40'}`}>
    {done ? '✓' : active ? <Loader2 className="w-3 h-3 animate-spin" /> : '○'}
    <span>{children}</span>
  </div>
);

export default SellModal;

